'use client';
import { useState } from 'react';
import { CustomLink } from '~/components/common/custom-link';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '~/components/ui/accordion';
import { Alert } from '~/components/ui/alert';
import { Card, CardContent } from '~/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { API_BASE_URL } from '~/config';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

export interface ImageApiReferenceProps {
  readonly modelName: string;
  defaultResolution: number;
  controlnetValues: string[];
}

interface FieldProps {
  name: string;
  type: string;
  description: string;
  required: boolean;
  example?: string;
  default_?: string;
  range?: string;
}

const Field = ({ name, type, description, required, example, default_, range }: FieldProps) => (
  <Accordion type="single" collapsible className="w-full">
    <AccordionItem value={name}>
      <AccordionTrigger>
        <span className="font-mono">
          {name}{' '}
          <span className="font-mono text-xs italic text-gray-500">
            ({type})
            {required ? (
              <span className="text-xs text-red-500"> Required</span>
            ) : (
              <span className="text-xs text-gray-500"> Optional</span>
            )}
          </span>
        </span>
      </AccordionTrigger>
      <AccordionContent className="flex flex-col">
        {default_ !== undefined && (
          <span className="text-sm text-gray-500">
            Default: <span className="font-mono">{default_}</span>
          </span>
        )}
        {range !== undefined && (
          <span className="text-sm text-gray-500">
            Range: <span className="font-mono">{range}</span>
          </span>
        )}
        {example !== undefined && (
          <span className="text-sm text-gray-500">
            Example: <span className="font-mono">{example}</span>
          </span>
        )}
        <span className="mt-4 text-sm text-gray-500">{description}</span>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
);

const successSchema = `{
  "title": "Response",
  "type": "array",
  "items": {
    "type": "object",
    "properties": {
      "id": {
        "type": "string",
        "description": "The unique identifier for the request."
      },
      "base64": {
        "type": "string",
        "description": "The base64 representation of the image."
      },
      "finishReason": {
        "type": "string", 
        "enum": ["CONTENT_FILTERED", "SUCCESS"],
        "description": "The reason the image generation was completed."
      },
      "seed": {
        "type": "string",
        "description": "The seed used in the image generation process."  
      }
    },
    "required": ["id", "base64", "finishReason", "seed"]
  }
}`;

const errorSchema = `{
  "properties": {
    "object": {
      "default": "error",
      "title": "Object",
      "type": "string"
    },
    "type": {
      "default": "invalid_request_error",
      "title": "Type",
      "type": "string"
    },
    "message": {
      "title": "Message",
      "type": "string"
    }
  },
  "required": ["message"],
  "title": "Error",
  "type": "object"
}`;

const ImageResponse = () => {
  const responseCodes = [
    { code: 200, description: 'Success' },
    { code: 400, description: 'Invalid Request Error', body: '' },
    {
      code: 401,
      description: 'Authentication Error',
      body: 'Ensure that you have properly set the `Authentication` header with a `Bearer` token from the Fireworks console.',
    },
    { code: 403, description: 'Permission Error', body: '' },
    {
      code: 404,
      description: 'Invalid Request Error',
      body: 'Ensure that your URL is correct and ensure that the {account} and {model} exist.',
    },
    {
      code: 415,
      description: 'Unsupported Media Type Error',
      body: 'This error is usually caused by specifying the wrong `Content-Type` header. Ensure that you are using `multipart/form-data` for `image_to_image` and `control_net` and `application/json` for `text_to_image`.',
    },
    {
      code: 429,
      description: 'Rate Limit Error',
      body: 'You have made too many requests. Lower your rate of making requests.',
    },
    {
      code: 500,
      description: 'Internal Server Error',
      body: 'The Fireworks server has encountered an error. Please contact us with more information about your request.',
    },
    {
      code: 502,
      description: 'Bad Gateway Error',
      body: 'The Fireworks server has encountered an error. Please contact us with more information about your request.',
    },
    {
      code: 503,
      description: 'Service Unavailable Error',
      body: 'The Fireworks server is unavailable. This may be because of an outage or because the service is overloaded. Try again later.',
    },
  ];

  const [acceptType, setAcceptType] = useState<string>('image/jpeg');
  return (
    <div className="mt-8">
      <h2 className="mb-4 text-xl font-semibold">Response Codes</h2>
      {responseCodes.map((codeInfo) => (
        <Accordion key={codeInfo.code} type="single" collapsible className="w-full">
          <AccordionItem value={codeInfo.code.toString()}>
            <AccordionTrigger>
              <span className="font-mono">
                {codeInfo.code === 200 ? (
                  <CheckCircleIcon className="mr-2 inline-block h-5 w-5 text-green-500" />
                ) : (
                  <ExclamationCircleIcon className="mr-2 inline-block h-5 w-5 text-red-500" />
                )}
                {codeInfo.code} - {codeInfo.description}
              </span>
            </AccordionTrigger>
            <AccordionContent className="pl-7">
              {codeInfo.code === 200 && (
                <div>
                  <p className="mb-4">
                    The response body of the request will depend on the specified <code>Accept:</code> header.
                  </p>

                  <div className="mb-4 flex items-center">
                    <label htmlFor="accept-header" className="mr-2">
                      Accept:
                    </label>
                    <Select onValueChange={(value) => setAcceptType(value)}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Select accept type" />
                      </SelectTrigger>
                      <SelectContent className="z-[100]">
                        <SelectItem value="image/jpeg">image/jpeg</SelectItem>
                        <SelectItem value="image/png">image/png</SelectItem>
                        <SelectItem value="application/json">application/json</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {acceptType === 'application/json' && (
                    <div className="mt-4">
                      <p className="mb-4">
                        For <code>application/json</code>, the response will be a JSON object with the following schema:
                      </p>

                      <Card className="mb-6">
                        <CardContent className="py-4">
                          <pre className="break-anywhere whitespace-pre-wrap font-mono text-xs">{successSchema}</pre>
                        </CardContent>
                      </Card>

                      <p className="mb-4">
                        <span className="font-semibold">base64:</span> Includes a base64-encoded string containing an
                        image in PNG format. To retrieve the image, base64-decode the string into binary data, then load
                        that binary data as a PNG file.
                      </p>
                      <p className="mb-4">
                        <span className="font-semibold">finishReason:</span> Specifies the outcome of the image
                        generation process. It could be <code>SUCCESS</code> indicating that the image was successfully
                        generated, or <code>CONTENT_FILTERED</code> if the image was filtered due to the{' '}
                        <code>safety_check=true</code> parameter being set.
                      </p>
                    </div>
                  )}

                  {(acceptType === 'image/png' || acceptType === 'image/jpeg') && (
                    <div className="mt-4">
                      <p className="mb-4">
                        When the <code>Accept</code> type is <code>{acceptType}</code>, the response body will contain a
                        binary image. Additionally, the response will include headers such as:
                      </p>
                      <p className="mb-4">
                        <span className="font-semibold">Content-Length:</span> Represents the length of the binary image
                        content.
                      </p>
                      <p className="mb-4">
                        <span className="font-semibold">Seed:</span> The random seed used to generate the image.
                      </p>
                      <p className="mb-4">
                        <span className="font-semibold">Finish-Reason:</span> Indicates the outcome of the image
                        generation, such as <code>CONTENT_FILTERED</code> or <code>SUCCESS</code>.
                      </p>
                    </div>
                  )}
                </div>
              )}
              {codeInfo.code === 400 && (
                <div className="mt-4">
                  <p className="mb-4">
                    You have sent an invalid request to the server. The response body will be a JSON object with the
                    following schema:
                  </p>

                  <Card className="mb-6">
                    <CardContent className="py-4">
                      <pre className="break-anywhere whitespace-pre-wrap font-mono text-xs">{errorSchema}</pre>
                    </CardContent>
                  </Card>

                  <p className="mb-4">
                    The <code>message</code> field will contain a human-readable error message and more details about
                    how to fix the error.
                  </p>
                </div>
              )}
              {codeInfo.code !== 200 && codeInfo.code !== 400 && <p>{codeInfo.body}</p>}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ))}
    </div>
  );
};

const requestHeaderFields: FieldProps[] = [
  {
    name: 'Authorization',
    type: 'string',
    description: 'Bearer authentication token to use when calling the service. Must be formatted as "Bearer {token}".',
    example: 'Bearer <API_KEY>>',
    required: true,
  },
  {
    name: 'Accept',
    type: 'one of "image/jpeg", "image/png", "application/json"',
    description:
      'Specifies which format to return the response in. With `image/png` and `image/jpeg`, the server will populate the response body with a binary image of the specified format. With `application/json`, the server will populate the response body with schema {base64: [string, ...], finishReason: string enum [SUCCESS, CONTENT_FILTERED], seed: int}. `base64` contains a list of base64-encoded image files in PNG format. `finishReason` is SUCCESS unless `safety_check=true` and the image response was filtered. `seed` is the seed that was used when generating the image.',
    example: 'image/jpeg',
    required: false,
  },
  // {
  //   name: 'Huggingface-Access-Key',
  //   type: 'string',
  //   description: 'HuggingFace access key to use to download a LoRA adapter.',
  //   required: false,
  // },
  {
    name: 'Jpeg-Quality',
    type: 'integer',
    description: 'Quality (percent) of the JPEG image to return. Only used when Accept=image/jpeg.',
    example: '95',
    range: '0-100',
    required: false,
  },
  {
    name: 'Json-Return-Image-Accept',
    type: 'one of "image/jpeg", "image/png"',
    description: 'MIME type for the image to return in the JSON response. Only used when Accept=application/json.',
    example: 'image/jpeg',
    required: false,
  },
];

interface TextToImageApiExampleProps {
  modelName: string;
  defaultResolution: number;
}

const commonImageFields: FieldProps[] = [
  {
    name: 'cfg_scale',
    type: 'float',
    description: 'Classifier-free guidance scale for the image diffusion process.',
    default_: '7',
    example: '7',
    required: false,
  },
  {
    name: 'sampler',
    type: 'one of null, "K_EULER", "K_DPMPP_2M"',
    description: 'Diffusion scheduler to use for the image generation process.',
    default_: 'K_EULER',
    example: '7',
    required: false,
  },
  {
    name: 'samples',
    type: 'integer',
    description: 'Number of images to generate.',
    default_: '1',
    example: '1',
    range: '1+',
    required: false,
  },
  {
    name: 'seed',
    type: 'integer',
    description: 'Random seed to use for the image generation process. If 0, we will use a totally random seed.',
    default_: '0',
    example: '0',
    required: false,
  },
  {
    name: 'steps',
    type: 'integer',
    description: 'Number of steps to use for the image generation process.',
    default_: '50',
    example: '30',
    range: '1+',
    required: false,
  },
  {
    name: 'safety_check',
    type: 'boolean',
    description:
      'Enable a safety check for each response. If the safety check model detects unsafe content, the response will be filtered with Finish-Reason = CONTENT_FILTERED.',
    default_: 'false',
    example: 'true',
    required: false,
  },
  // {
  //   name: 'lora_adapter_name (experimental)',
  //   type: 'string',
  //   description:
  //     'A huggingface name (e.g. jbilcke-hf/sdxl-botw) that specifies which LoRA adapter to load and run in SDXL inference.',
  //   default_: 'null',
  //   example: 'jbilcke-hf/sdxl-botw',
  //   required: false,
  // },
  // {
  //   name: 'lora_weight_filename (experimental)',
  //   type: 'string',
  //   description:
  //     'The filename (e.g. lora.safetensors) of the file within the HF repo to load as the HF adapter. Only `safetensors` files are supported.',
  //   default_: 'null',
  //   example: 'lora.safetensors',
  //   required: false,
  // },
];

const textToImageFields: FieldProps[] = [
  {
    name: 'prompt',
    type: 'string',
    description: 'Prompt to use for the image generation process.',
    example: 'A photo of a cat',
    required: true,
  },
  {
    name: 'negative_prompt',
    type: 'string',
    description: 'Negative prompt to use for the image generation process.',
    example: 'A photo of a dog',
    required: false,
  },
  {
    name: 'height',
    type: 'integer',
    description:
      'Height of the image in pixels. Supported resolutions (width, height) are (1024, 1024), (1152, 896), (896, 1152), (1216, 832), (832, 1216), (1344, 768), (768, 1344), (1536, 640), and (640, 1536).',
    default_: '1024',
    example: '1024',
    range: '512-1536',
    required: false,
  },
  {
    name: 'width',
    type: 'integer',
    description:
      'width of the image in pixels. Supported resolutions (width, height) are (1024, 1024), (1152, 896), (896, 1152), (1216, 832), (832, 1216), (1344, 768), (768, 1344), (1536, 640), and (640, 1536).',
    default_: '1024',
    example: '1024',
    range: '512-1536',
    required: false,
  },
];

const TextToImageApiReference = ({ modelName, defaultResolution }: TextToImageApiExampleProps) => {
  const textToImageFieldsWithResolution = textToImageFields.map((field) => {
    if (field.name === 'height' || field.name === 'width') {
      return { ...field, default_: defaultResolution.toString() };
    }
    return field;
  });

  return (
    <div>
      <p className="mb-4 text-sm">Generate an image from a text prompt</p>
      <Card className="mb-8">
        <CardContent className="p-4">
          <pre className="break-anywhere whitespace-pre-wrap font-mono text-xs">
            POST {API_BASE_URL}/inference/v1/image_generation/{modelName} HTTP/1.1
          </pre>
        </CardContent>
      </Card>
      <div className="mt-4">
        <h3 className="mb-4 text-lg font-semibold">Request Headers</h3>
        {requestHeaderFields.map((field, index) => (
          <Field key={index} {...field} />
        ))}
      </div>
      <div className="mt-8">
        <h3 className="mb-4 text-lg font-semibold">Request Parameters</h3>
        {[...textToImageFieldsWithResolution, ...commonImageFields].map((field, index) => (
          <Field key={index} {...field} />
        ))}
      </div>
      <ImageResponse />
    </div>
  );
};
interface ImageToImageApiExampleProps {
  modelName: string;
}

const imageToImageFields: FieldProps[] = [
  {
    name: 'init_image',
    type: 'file',
    description: 'Image used to initialize the diffusion process, in lieu of random noise.',
    required: true,
  },
  {
    name: 'init_image_mode',
    type: 'one of "IMAGE_STRENGTH", "STEP_SCHEDULE"',
    description:
      'The mode to run the transformation process in. With IMAGE_STRENGTH, the `image_strength` parameter must be specified, which controls the fraction of steps to use image context. With STEP_SCHEDULE, `step_schedule_start` and `step_schedule_end` must be specified, which specify the beginning and end steps in which to run the diffusion process.',
    default_: 'IMAGE_STRENGTH',
    example: 'IMAGE_STRENGTH',
    required: false,
  },
  {
    name: 'image_strength',
    type: 'float',
    description:
      'Fraction of steps to use image context. MUST be specified when init_image_mode=IMAGE_STRENGTH and should not be specified otherwise.',
    default_: 'null',
    example: '0.5',
    range: '0-1',
    required: false,
  },
  {
    name: 'step_schedule_start',
    type: 'float',
    description:
      'Fraction of steps at the beginning to use init_image context. MUST be specified when init_image_mode=STEP_SCHEDULE and should not be specified otherwise.',
    default_: '0.65',
    example: '0.65',
    range: '0-1',
    required: false,
  },
  {
    name: 'step_schedule_end',
    type: 'float',
    description:
      'Fraction of steps at the end to use init_image context. MUST be specified when init_image_mode=STEP_SCHEDULE and should not be specified otherwise.',
    default_: 'null',
    example: '0.9',
    range: '0-1',
    required: false,
  },
];

const ImageToImageApiReference = ({ modelName }: ImageToImageApiExampleProps) => {
  return (
    <div>
      <p className="mb-4 text-sm">Transform an image based on a text prompt</p>
      <Card className="mb-8">
        <CardContent className="p-4">
          <pre className="break-anywhere whitespace-pre-wrap font-mono text-xs">
            POST {API_BASE_URL}/inference/v1/image_generation/{modelName}/image_to_image HTTP/1.1
          </pre>
        </CardContent>
      </Card>
      <div className="mt-4">
        <h3 className="mb-4 text-lg font-semibold">Request Headers</h3>
        {requestHeaderFields.map((field, index) => (
          <Field key={index} {...field} />
        ))}
      </div>
      <div className="mt-8">
        <h3 className="mb-4 text-lg font-semibold">Request Parameters</h3>
        {[...imageToImageFields, ...commonImageFields].map((field, index) => (
          <Field key={index} {...field} />
        ))}
      </div>
      <ImageResponse />
    </div>
  );
};
interface ControlNetApiExampleProps {
  modelName: string;
}

const controlNetFields: FieldProps[] = [
  {
    name: 'prompt',
    type: 'string',
    description: 'Prompt to use for the image generation process.',
    example: 'A photo of a cat',
    required: true,
  },
  {
    name: 'control_image',
    type: 'file',
    description: 'Image to use as a guidance for ControlNet.',
    required: true,
  },
  {
    name: 'control_net_name',
    type: 'one of "canny" or "qr"',
    description: 'Which ControlNet to use.',
    default_: 'canny',
    example: 'canny',
    required: false,
  },
  {
    name: 'conditioning_scale',
    type: 'float',
    description: 'Scalar from 0 to 1 of the guidance strength from control image.',
    default_: '0.5',
    example: '0.5',
    range: '0-1',
    required: false,
  },
  {
    name: 'step_schedule_start',
    type: 'float',
    description: 'Fraction of steps at the beginning to skip.',
    default_: '0.0',
    example: '0',
    range: '0-steps',
    required: false,
  },
  {
    name: 'step_schedule_end',
    type: 'float',
    description: 'Fraction of steps at the end to skip.',
    default_: '1.0',
    example: '30',
    range: '0-steps',
    required: false,
  },
];

const ControlNetApiReference = ({ modelName }: ControlNetApiExampleProps) => {
  return (
    <div>
      <p className="mb-4 text-sm">Generate an image based on a text prompt and a control image.</p>
      <Card className="mb-8">
        <CardContent className="p-4">
          <pre className="break-anywhere whitespace-pre-wrap font-mono text-xs">
            POST {API_BASE_URL}/inference/v1/image_generation/{modelName}/control_net HTTP/1.1
          </pre>
        </CardContent>
      </Card>
      <div className="mt-4">
        <h3 className="mb-4 text-lg font-semibold">Request Headers</h3>
        {requestHeaderFields.map((field, index) => (
          <Field key={index} {...field} />
        ))}
      </div>
      <div className="mt-8">
        <h3 className="mb-4 text-lg font-semibold">Request Parameters</h3>
        {[...controlNetFields, ...commonImageFields].map((field, index) => (
          <Field key={index} {...field} />
        ))}
      </div>
      <ImageResponse />
    </div>
  );
};

const cannyEdgeFields: FieldProps[] = [
  {
    name: 'image',
    type: 'file',
    description: 'Image to run edge detection on.',
    required: true,
  },
  {
    name: 'min_val',
    type: 'float',
    description:
      'Scalar minimum threshold for edge detection. Equivalent to `threshold1` in OpenCV. See the OpenCV documentation for more details: https://docs.opencv.org/4.x/dd/d1a/group__imgproc__feature.html#ga04723e007ed888ddf11d9ba04e2232de.',
    default_: '100.0',
    example: '100.0',
    required: false,
  },
  {
    name: 'max_val',
    type: 'float',
    description:
      'Scalar maximum threshold for edge detection. Equivalent to `threshold2` in OpenCV. See the OpenCV documentation for more details: https://docs.opencv.org/4.x/dd/d1a/group__imgproc__feature.html#ga04723e007ed888ddf11d9ba04e2232de.',
    default_: '100.0',
    example: '100.0',
    required: false,
  },
];

const cannyHeaderFields: FieldProps[] = [
  {
    name: 'Authorization',
    type: 'string',
    description: 'Bearer authentication token to use when calling the service. Must be formatted as "Bearer {token}".',
    example: 'Bearer <API_KEY>',
    required: true,
  },
  {
    name: 'Accept',
    type: 'one of "image/jpeg", "image/png", "application/json"',
    description:
      'Specifies which format to return the response in. With `image/png` and `image/jpeg`, the server will populate the response body with a binary image of the specified format. With `application/json`, the server will populate the response body with schema {base64: [string, ...], finishReason: string enum [SUCCESS, CONTENT_FILTERED], seed: int}. `base64` contains a list of base64-encoded image files in PNG format. `finishReason` is SUCCESS unless `safety_check=true` and the image response was filtered. `seed` is the seed that was used when generating the image.',
    example: 'image/jpeg',
    required: false,
  },
  // {  FIXME: support
  //   name: 'Jpeg-Quality',
  //   type: 'integer',
  //   description: 'Quality (percent) of the JPEG image to return. Only used when Accept=image/jpeg.',
  //   example: '95',
  //   range: '0-100',
  //   required: false,
  // },
  // {  FIXME: support
  //   name: 'Json-Return-Image-Accept',
  //   type: 'one of "image/jpeg", "image/png"',
  //   description: 'MIME type for the image to return in the JSON response. Only used when Accept=application/json.',
  //   example: 'image/jpeg',
  //   required: false,
  // },
];

interface CannyEdgeDetectionApiReferenceProps {
  modelName: string;
}

const CannyEdgeDetectionApiReference = ({ modelName }: CannyEdgeDetectionApiReferenceProps) => {
  return (
    <div>
      <p className="mb-4 text-sm">Generate an image based on a text prompt and a control image.</p>
      <Card className="mb-8">
        <CardContent className="p-4">
          <pre className="break-anywhere whitespace-pre-wrap font-mono text-xs">
            POST {API_BASE_URL}/inference/v1/image_generation/{modelName}/canny_edge_detection HTTP/1.1
          </pre>
        </CardContent>
      </Card>
      <div className="mt-4">
        <h3 className="mb-4 text-lg font-semibold">Request Headers</h3>
        {cannyHeaderFields.map((field, index) => (
          <Field key={index} {...field} />
        ))}
      </div>
      <div className="mt-8">
        <h3 className="mb-4 text-lg font-semibold">Request Parameters</h3>
        {cannyEdgeFields.map((field, index) => (
          <Field key={index} {...field} />
        ))}
      </div>
    </div>
  );
};

const qrFields: FieldProps[] = [
  {
    name: 'prompt',
    type: 'string',
    description: 'Text data to encode into the QR code.',
    required: true,
  },
  {
    name: 'width',
    type: 'int',
    description: 'Width of the QR code image in pixels.',
    default_: '1024',
    example: '1024',
    required: false,
  },
  {
    name: 'height',
    type: 'int',
    description: 'Height of the QR code image in pixels.',
    default_: '1024',
    example: '1024',
    required: false,
  },
];

const qrHeaderFields: FieldProps[] = [
  {
    name: 'Authorization',
    type: 'string',
    description: 'Bearer authentication token to use when calling the service. Must be formatted as "Bearer {token}".',
    example: 'Bearer <API_KEY>',
    required: true,
  },
  {
    name: 'Accept',
    type: 'one of "image/jpeg", "image/png"',
    description:
      'Specifies which format to return the response in. With `image/png` and `image/jpeg`, the server will populate the response body with a binary image of the specified format. With `application/json`, the server will populate the response body with schema {base64: [string, ...], finishReason: string enum [SUCCESS, CONTENT_FILTERED], seed: int}. `base64` contains a list of base64-encoded image files in PNG format. `finishReason` is SUCCESS unless `safety_check=true` and the image response was filtered. `seed` is the seed that was used when generating the image.',
    example: 'image/jpeg',
    required: false,
  },
];

interface QRCodeDetectionApiReferenceProps {
  modelName: string;
}

const QRCodeDetectionApiReference = ({ modelName }: QRCodeDetectionApiReferenceProps) => {
  return (
    <div>
      <p className="mb-4 text-sm">Generate a QR Code image with the provided text.</p>
      <Card className="mb-8">
        <CardContent className="p-4">
          <pre className="break-anywhere whitespace-pre-wrap font-mono text-xs">
            POST {API_BASE_URL}/inference/v1/image_generation/{modelName}/qr_code HTTP/1.1
          </pre>
        </CardContent>
      </Card>
      <div className="mt-4">
        <h3 className="mb-4 text-lg font-semibold">Request Headers</h3>
        {qrHeaderFields.map((field, index) => (
          <Field key={index} {...field} />
        ))}
      </div>
      <div className="mt-8">
        <h3 className="mb-4 text-lg font-semibold">Request Parameters</h3>
        {qrFields.map((field, index) => (
          <Field key={index} {...field} />
        ))}
      </div>
    </div>
  );
};

export const ImageApiReference = ({ modelName, defaultResolution, controlnetValues }: ImageApiReferenceProps) => {
  const [additionalConditioning, setAdditionalConditioning] = useState<string>('None');

  const handleAdditionalConditioningChange = (value: string | null) => {
    if (value === null) {
      value = 'None';
    }
    setAdditionalConditioning(value);
  };

  return (
    <div className="mb-20 grid">
      <div>
        <h2 className="mb-4 text-xl font-semibold">Calling the API</h2>
        <p className="mb-4">
          The Fireworks image generation API can be called by making HTTP requests from any language. We also provide a{' '}
          <CustomLink href="https://pypi.org/project/fireworks-ai/">Python client library</CustomLink> that you can use
          to easily call the API.
        </p>

        <p className="mb-4">
          Below we describe the raw REST API for making requests to the API. For easy generation of calls, see the
          console above.
        </p>

        <h2 className="mb-4 text-xl font-semibold">Authentication</h2>
        <p className="mb-4">
          All API calls require a valid authentication token. You can obtain a token from the{' '}
          <CustomLink href="/api-keys">Fireworks console</CustomLink>. When calling the API, make sure to specify the{' '}
          <span className="font-mono">Authentication</span> header, with your token after the word{' '}
          <span className="font-mono">Bearer</span>. For example:{' '}
          <span className="font-mono">Authorization: Bearer abcd1234defg5678hijk9012lmno3456pqrs7890tuvw1234</span>.
        </p>

        <h2 className="mb-4 text-xl font-semibold">File Uploads (image_to_image and control_net)</h2>
        <p className="mb-4">
          Since <span className="font-mono">image_to_image</span> and <span className="font-mono">control_net</span>{' '}
          require uploading binary image files, the APIs are designed around the{' '}
          <CustomLink href="https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types#multipartform-data">
            multipart/form-data
          </CustomLink>{' '}
          content type. This means that forming your query is not as simple as writing a JSON response body, as with
          text-to-image. See information about how to make these requests in{' '}
          <CustomLink href="https://stackoverflow.com/questions/12385179/how-to-send-a-multipart-form-data-with-requests-in-python">
            Python requests
          </CustomLink>
          , <CustomLink href="https://stackoverflow.com/a/35206069">node.js fetch</CustomLink>, and{' '}
          <CustomLink href="https://everything.curl.dev/http/multipart">cURL</CustomLink>.
        </p>
        <Alert variant="destructive" className="mb-4">
          A common pitfall with <span className="font-mono">multipart/form-data</span> is to try to explicitly set{' '}
          <span className="font-mono">Content-Type: multipart/form-data</span> in your request. This is incorrect, as
          the `Content-Type` requires a boundary string to be specified as well. Instead, use the form submission API of
          the request library you're using, for example, the{' '}
          <CustomLink href="https://stackoverflow.com/questions/12385179/how-to-send-a-multipart-form-data-with-requests-in-python">
            Python requests
          </CustomLink>{' '}
          modules forms a <span className="font-mono">multipart/form-data</span> request when you include{' '}
          <span className="font-mono">files</span> as a request parameter.
        </Alert>
        <Alert variant="destructive" className="mb-4">
          Another common pitfall with <span className="font-mono">multipart/form-data</span> is to erroneously set{' '}
          <span className="font-mono">Content-Type: application/json</span> in your request, especially if you are
          directly porting from the text-to-image API. The service will return an HTTP 415 Unsupported Media Type
          response if you specify the wrong `Content-Type`.
        </Alert>
      </div>
      <div className="mt-6">
        <h2 className="mb-2 text-xl font-semibold">Image Generation Modes</h2>
        <AdditionalConditioningSelector
          onSelect={handleAdditionalConditioningChange}
          showHeader={false}
          controlnetValues={controlnetValues}
          showControlnetEndpoints={true}
        />
      </div>

      {additionalConditioning === 'None' && (
        <TextToImageApiReference modelName={modelName} defaultResolution={defaultResolution} />
      )}
      {additionalConditioning === 'Image-to-Image' && <ImageToImageApiReference modelName={modelName} />}
      {additionalConditioning === 'ControlNet' && <ControlNetApiReference modelName={modelName} />}
      {additionalConditioning === 'CannyEdgeDetection' && <CannyEdgeDetectionApiReference modelName={modelName} />}
      {additionalConditioning === 'QRCode' && <QRCodeDetectionApiReference modelName={modelName} />}
    </div>
  );
};

interface Props {
  onSelect: (value: string | null) => void;
  showHeader: boolean;
  controlnetValues: string[];
  showControlnetEndpoints: boolean;
}

const AdditionalConditioningSelector: React.FC<Props> = ({
  onSelect,
  showHeader,
  controlnetValues,
  showControlnetEndpoints,
}) => {
  const [conditioning, setConditioning] = useState<string | null>('None');

  const handleChange = (newConditioning: string) => {
    setConditioning(newConditioning);
    onSelect(newConditioning);
  };

  const showControlnet = controlnetValues.length > 0;
  const showCanny = controlnetValues.includes('canny');
  const showQR = controlnetValues.includes('qr');

  return (
    <div>
      {showHeader && <h2 className="mb-4 text-xl font-semibold">Additional Conditioning</h2>}
      <Tabs value={conditioning || undefined} onValueChange={handleChange}>
        <TabsList>
          <TabsTrigger value="None">None</TabsTrigger>
          <TabsTrigger value="Image-to-Image">Image-to-Image</TabsTrigger>
          {showControlnet && <TabsTrigger value="ControlNet">ControlNet</TabsTrigger>}
          {showControlnetEndpoints && showCanny && (
            <TabsTrigger value="CannyEdgeDetection">Canny Edge Detection</TabsTrigger>
          )}
          {showControlnetEndpoints && showQR && <TabsTrigger value="QRCode">QR Code</TabsTrigger>}
        </TabsList>
        <TabsContent value="None" />
        <TabsContent value="Image-to-Image" />
        {showControlnet && <TabsContent value="ControlNet" />}
        {showControlnetEndpoints && showCanny && <TabsContent value="CannyEdgeDetection" />}
        {showControlnetEndpoints && showQR && <TabsContent value="QRCode" />}
      </Tabs>
    </div>
  );
};
